import { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import parse from "html-react-parser"

import classNames from "classnames"

import { LoadLiveChannels } from "shared/components/stored_fields/service_account_select_field/api"
import { buildConnectionPath, windowFeatures } from "shared/reconnect_window"
import groupBy from "lodash/groupBy"

import { planNameBadge } from "shared/utils/tiers"
import newFeatureBadge from "images/plan-badges/new-badge.svg?raw"

import { verifyIntendedPromiseRejection } from "app/components/diy_composer/utils/modal_promise"

import {
  trackServiceConnectionStarted,
  trackServiceConnectionCompleted,
  trackServiceConnectionFailed,
} from "app/utils/mixpanel_tracking"

export default function ServiceAccountSelectField({
  field,
  fieldOwnerServiceId,
  fieldOwnerServiceName,
  onChange,
  updateServiceToForceRetry,
  userAllowMultipleLiveChannels,
  proFeatureGate,
  isOnColor = false,
}) {
  let popup = null
  const liveChannels = field.options
  const findAccount = id => liveChannels?.find(lc => lc.id === id)
  const [selectedAccount, updateSelectedAccount] = useState(
    findAccount(field.value)
  )
  const accountLabel = account => (account ? (account.offline ? "reconnect" : "add") : "add")
  const [accountActionLabel, updateAccountActionLabel] = useState(
    accountLabel(selectedAccount)
  )

  const connectionAttemptRef = useRef(null)
  const getConnectionContext = () => {
    const owner = field?.owner
    if (typeof owner !== "string") return "manual"

    if (owner.includes("/triggers/")) return "trigger"
    if (owner.includes("/actions/")) return "action"
    if (owner.includes("/queries/")) return "query"

    return "manual"
  }

  useEffect(() => {
    if (selectedAccount?.id) onChange(selectedAccount.id, true)
  }, [])

  const onAccountChange = evt => {
    const liveChannel = findAccount(evt.target.value)
    if (!liveChannel) return

    dispatchAccountChange(liveChannel)
  }

  const dispatchAccountChange = liveChannel => {
    updateSelectedAccount(liveChannel)
    updateAccountActionLabel(accountLabel(liveChannel))
    onChange(liveChannel.id, true)
  }

  const connectionPath = buildConnectionPath(
    fieldOwnerServiceId,
    accountActionLabel === "reconnect" ? selectedAccount?.id ?? "" : "",
    field.connectionFinishedUrl
  )

  const reloadServiceAccountsList = () => {
    function showFailureFlash(errorType = "reload_failed") {
      const attempt = connectionAttemptRef.current

      if (attempt) {
        trackServiceConnectionFailed({
          serviceName: fieldOwnerServiceName,
          serviceModuleName: fieldOwnerServiceId,
          authType: attempt.authType || "oauth",
          connectionContext: attempt.connectionContext || "manual",
          isNewConnection: attempt.isNewConnection,
          entrypoint: "published_applet_enable",
          errorType,
        })
        connectionAttemptRef.current = null
      }

      window.appendFlash(
        "Failed to reload your accounts. Please reload the page and try again.",
        "danger"
      )
    }

    LoadLiveChannels(fieldOwnerServiceId)
      .then(response => {
        if (!response.data) {
          showFailureFlash()
        } else {
          const reloadedLiveChannels = response.data.channel.live_channels
          const duplicatedConnection = Object.values(
            groupBy(
              reloadedLiveChannels.filter(lc => !lc.offline),
              "user_name_field"
            )
          ).find(lcs => lcs.length > 1)

          field.updateOptionsList(reloadedLiveChannels)

          if (accountActionLabel === "add") {
            dispatchAccountChange(
              reloadedLiveChannels[reloadedLiveChannels.length - 1]
            )
          } else {
            updateAccountActionLabel("add") // reset button label
            updateServiceToForceRetry?.(true)
          }

          const attempt = connectionAttemptRef.current || {}
          const timeToConnect =
            typeof attempt.startTime === "number" ? Date.now() - attempt.startTime : undefined

          if (duplicatedConnection) {
            trackServiceConnectionFailed({
              serviceName: fieldOwnerServiceName,
              serviceModuleName: fieldOwnerServiceId,
              authType: attempt.authType || "oauth",
              connectionContext: attempt.connectionContext || "manual",
              isNewConnection: attempt.isNewConnection,
              entrypoint: "published_applet_enable",
              errorType: "duplicate_account",
            })
            connectionAttemptRef.current = null

            window.appendFlash(
              `You added a duplicate account. Log into a new account on ${fieldOwnerServiceName} and try again.`,
              "success"
            )
          } else {
            const liveChannelId = attempt.isNewConnection
              ? reloadedLiveChannels[reloadedLiveChannels.length - 1]?.id
              : reloadedLiveChannels.find(lc => lc.id === attempt.attemptedAccountId)?.id

            trackServiceConnectionCompleted({
              serviceName: fieldOwnerServiceName,
              serviceModuleName: fieldOwnerServiceId,
              authType: attempt.authType || "oauth",
              connectionContext: attempt.connectionContext || "manual",
              isNewConnection: !!attempt.isNewConnection,
              inDiyFlow: false,
              entrypoint: "published_applet_enable",
              liveChannelId,
              timeToConnect,
            })
            connectionAttemptRef.current = null

            window.appendFlash("Service connected successfully", "success")
          }
        }
      })
      .catch(showFailureFlash)
  }

  const onAddAccountClick = e => {
    e.preventDefault()

    if (field.showNewBadge) {
      field.hideNewBadge()
    }

    if (
      accountActionLabel !== "reconnect" &&
      userAllowMultipleLiveChannels === false
    ) {
      verifyIntendedPromiseRejection(proFeatureGate("multi_service_account"))
    } else {
      const authType = "oauth"
      const isNewConnection = accountActionLabel !== "reconnect"
      const connectionContext = getConnectionContext()

      connectionAttemptRef.current = {
        startTime: Date.now(),
        authType,
        connectionContext,
        isNewConnection,
        attemptedAccountId: selectedAccount?.id,
      }

      trackServiceConnectionStarted({
        serviceName: fieldOwnerServiceName,
        serviceModuleName: fieldOwnerServiceId,
        authType,
        connectionContext,
        isNewConnection,
        entrypoint: "published_applet_enable",
      })

      if (popup == null || popup.closed) {
        popup = window.open(
          connectionPath,
          `${fieldOwnerServiceId}_connection`,
          windowFeatures
        )
      } else {
        popup.focus()
      }

      window.connectionFinished = reloadServiceAccountsList
      window.connectionFailed = (error = null) => {
        const attempt = connectionAttemptRef.current
        connectionAttemptRef.current = null

        if (attempt) {
          trackServiceConnectionFailed({
            serviceName: fieldOwnerServiceName,
            serviceModuleName: fieldOwnerServiceId,
            authType: attempt.authType || "oauth",
            connectionContext: attempt.connectionContext || "manual",
            isNewConnection: attempt.isNewConnection,
            entrypoint: "published_applet_enable",
            errorType: error?.message || "popup_connection_failed",
          })
        }

        window.appendFlash(
          `Could not connect ${fieldOwnerServiceName} right now. Please try again later.`,
          "danger"
        )
      }
    }
  }

  const label =
    accountActionLabel === "reconnect" ? "Reconnect account" : "Add new account"

  const iconForButton = () => {
    if (field.showNewBadge) {
      return <span>{parse(newFeatureBadge)}</span>
    } else if (
      accountActionLabel !== "reconnect" &&
      userAllowMultipleLiveChannels === false
    ) {
      // The badge asset carries its own colors, picked against the button's
      // surface (on-color buttons are white, so they take the dark cut).
      return (
        <span className="badge pro_plus">{parse(planNameBadge("Pro+", isOnColor ? "default" : "white"))}</span>
      )
    }
  }

  return (
    <>
      <select value={selectedAccount?.id ?? ""} onChange={onAccountChange}>
        {!selectedAccount && (
          <option value="" disabled>
            Please add an account
          </option>
        )}
        {liveChannels.map(live_channel => {
          return (
            <option key={live_channel.id} value={live_channel.id}>
              {live_channel.user_name_field}{" "}
              {live_channel.offline && `(Reconnect)`}
            </option>
          )
        })}
      </select>
      <div
        className={classNames("ingredients-text-field", {
          "ingredients-text-field-on-color": isOnColor,
        })}
      >
        <div className="tube">
          <button
            type="button"
            className={classNames("button-tiny", "button-compact", {
              "button-on-color": isOnColor,
              "new-badge-icon": field.showNewBadge,
            })}
            onClick={onAddAccountClick}
          >
            {iconForButton()}
            {label}
          </button>
        </div>
      </div>
    </>
  )
}

ServiceAccountSelectField.propTypes = {
  isOnColor: PropTypes.bool,
  field: PropTypes.shape({
    connectionFinishedUrl: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        offline: PropTypes.bool.isRequired,
        user_name_field: PropTypes.string.isRequired,
      })
    ).isRequired,
    updateOptionsList: PropTypes.func.isRequired,
    value: PropTypes.string,
    showNewBadge: PropTypes.bool,
    hideNewBadge: PropTypes.func,
  }),
  fieldOwnerServiceId: PropTypes.string.isRequired,
  fieldOwnerServiceName: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  updateServiceToForceRetry: PropTypes.func,
  userAllowMultipleLiveChannels: PropTypes.bool,
  proFeatureGate: PropTypes.func,
}
